import { useEffect, useMemo } from 'react';
import type { TableColumnsType, TablePaginationConfig } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AlertBadge,
  Button,
  CopyText,
  SectionBox,
  Table,
  Text,
  DetailsLinkButton,
} from '@/components';
import { useTranslation } from 'react-i18next';
import {
  CompCounterparty,
  CompCustomerBrief,
  ComplianceTransferRow,
  TxListRequest,
  TxRecheckRequest,
} from '@/api';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { complianceTransferActions } from '@/redux/actions';
import {
  complianceTransferSelectors,
  showDemoDataSelectors,
} from '@/redux/selectors';
import { formatUSD, getShortAddress, getShortHash } from '@/helpers';
import { authPagesPaths } from '@/config/navigationPages';

import { transfersDemoData } from '../../Transfers.constants';

import styles from './AllTransfers.module.sass';

const PAGE_SIZE = 10;

const AllTransfers = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const getCounterpartyName = (val: CompCounterparty): string => {
    if (!val) return '—';
    if (val.name) return val.name;
    if (val.address) return getShortAddress(val.address);
    return 'Unnamed cluster';
  };
  const { data: complianceTransferData, loading } = useAppSelector(
    complianceTransferSelectors.getComplianceTransferSlice,
  );

  const isShowDemoData = useAppSelector(showDemoDataSelectors.byCompliance);

  const data = useMemo(() => {
    return isShowDemoData ? transfersDemoData : complianceTransferData;
  }, [isShowDemoData, complianceTransferData]);

  const fetchList = (page: number = 1) => {
    const offset = (page - 1) * PAGE_SIZE;
    const limit = PAGE_SIZE;
    const payload: TxListRequest = {
      limit,
      offset,
    };
    dispatch(complianceTransferActions.fetchTransfers(payload));
  };

  useEffect(() => {
    if (!isShowDemoData) {
      fetchList();
    }
  }, [isShowDemoData]);

  const recheck = (id: string | number) => {
    if (loading || isShowDemoData) return;

    const payload: TxRecheckRequest = { id };
    dispatch(complianceTransferActions.txRecheck(payload));
  };

  const columns: TableColumnsType<ComplianceTransferRow> = [
    {
      title: t('translation.customer'),
      dataIndex: 'customer',
      ellipsis: true,
      render: (customers: CompCustomerBrief[]) => {
        return (
          <div className={styles.customers}>
            {customers.map((customer) => (
              <Text key={customer.id} weight="medium" wrap="ellipsis">
                {customer.identifier}
              </Text>
            ))}
          </div>
        );
      },
    },
    {
      title: t('translation.tx_hash'),
      dataIndex: 'txHash',
      ellipsis: true,
      render: (value, record) => (
        <div className={styles.txHashCell}>
          {!isShowDemoData && (
            <DetailsLinkButton
              onClick={() =>
                navigate(authPagesPaths.DETAILS_PAGE_PATH, {
                  state: { uid: record.uid, backPath: pathname },
                })
              }
            />
          )}
          <CopyText text={getShortHash(value)} copyText={value} />
        </div>
      ),
    },
    {
      title: t('translation.address'),
      dataIndex: 'address',
      ellipsis: true,
      render: (value) => (
        <CopyText text={getShortAddress(value)} copyText={value} />
      ),
    },
    {
      title: t('translation.counterparty'),
      dataIndex: 'counterparty',
      ellipsis: true,
      render: (value) => (
        <Text weight="medium" wrap="ellipsis" color="gray">
          {getCounterpartyName(value)}
        </Text>
      ),
    },
    {
      title: t('translation.direction'),
      dataIndex: 'direction',
      ellipsis: true,
    },
    {
      title: t('translation.alert'),
      dataIndex: 'level',
      align: 'center',
      sorter: {
        compare: (a, b) => a.level.length - b.level.length,
      },
      render: (value) => <AlertBadge type={value} />,
    },
    {
      title: t('translation.funds_transferred'),
      dataIndex: 'fundsTransferred',
      sorter: {
        compare: (a, b) => a.fundsTransferred - b.fundsTransferred,
      },
      render: (value) => formatUSD(value),
    },
    {
      title: t('translation.compliance_next_update'),
      dataIndex: 'nextUpdate',
      ellipsis: true,
      render: (value) => value,
    },
    {
      title: '',
      dataIndex: 'key',
      render: (value) => (
        <Button
          type="secondary"
          disabled={loading || isShowDemoData}
          size="s"
          onClick={() => recheck(value)}
        >
          {t('translation.action_recheck_btn')}
        </Button>
      ),
    },
  ];

  return (
    <SectionBox>
      {{
        title: t('translation.all_transfers'),
        body: (
          <Table
            columns={columns}
            dataSource={data?.transfers.txs}
            paginationConfig={{
              position: ['bottomLeft'],
              defaultCurrent: 1,
              pageSize: PAGE_SIZE,
              hideOnSinglePage: true,
              showSizeChanger: false,
              total: data?.stats?.count || 0,
              onChange: (page) => fetchList(page),
            }}
          />
        ),
      }}
    </SectionBox>
  );
};

export default AllTransfers;
